import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useFilters } from '../contexts/FilterContext';

// Shared plumbing for the Admin page's cards (Users, Store Groups, Accounting
// Profiles): the store picker options, the save bar, and the load → edit →
// dirty → save cycle against one GET/PUT endpoint.

export function useStoreOptions() {
  const { locations } = useFilters(); // server-scoped: owners only see their stores
  return useMemo(
    () => locations.map(l => ({ id: Number(l.ID), label: l.NAME, sub: l.CITY })),
    [locations]
  );
}

export function SaveBar({ dirty, saving, savedFlash, onSave, onDiscard }) {
  return (
    <div className="admin-savebar" style={{ marginTop: 12 }}>
      <button className="btn-primary inline" disabled={!dirty || saving} onClick={onSave}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
      {dirty && !saving && (
        <button className="btn-ghost" onClick={onDiscard}>Discard changes</button>
      )}
      {savedFlash && <span className="saved-flash">✓ Saved — changes are live</span>}
      {dirty && !savedFlash && <span className="hint">Unsaved changes</span>}
    </div>
  );
}

// Shared load/dirty/save plumbing for one endpoint + one editable list.
export function useEditableList(url, pickList, buildPayload) {
  const [meta, setMeta] = useState(null); // { editable, reason, ... }
  const [loadError, setLoadError] = useState(null);
  const [items, setItems] = useState([]);
  const [baseline, setBaseline] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const adopt = (data) => {
    setMeta(data);
    const list = pickList(data) || [];
    setItems(list);
    setBaseline(JSON.stringify(list));
  };

  useEffect(() => {
    axios.get(url)
      .then(res => adopt(res.data))
      .catch(err => setLoadError(err.response?.data?.error || err.message));
  }, [url]);

  const dirty = meta && JSON.stringify(items) !== baseline;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await axios.put(url, buildPayload(items));
      adopt({ ...meta, ...data });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setItems(JSON.parse(baseline));
    setSaveError(null);
  };

  return { meta, loadError, items, setItems, dirty, saving, saveError, setSaveError, savedFlash, save, discard };
}
